import type { Track } from '../../domain/icy-parse';
import { remainingMs } from '../../domain/sleep';
import { songQuery } from '../../domain/song-search';
import { findStation, type Station } from '../../domain/stations';
import { type Lang, translate } from '../../i18n';
import { el, icon } from '../../lib/dom';
import { type AppState, playableStations } from '../../store/app';
import { formatClock, formatStationDescription, formatTrack } from '../format';
import { createSongMenu } from './song-menu';
import { createVolume, type VolumeDeps } from './volume';

/**
 * Medijska transportna vrstica (`.nowplaying`, DS v4.20.0).
 *
 * Play polnilo je brand (`--primary` prek `.nowplaying__btn.play`), nikoli
 * statusna barva. »V živo« je status in nosi piko IN besedo.
 *
 * Razporeditev podatkov (P2 §3):
 *   naslov  → skladba, kadar jo postaja pove; sicer ime postaje
 *   podnapis→ V ŽIVO · (ime postaje ALI opis/stanje)
 *
 * ⚠️ **Podnapis nosi največ dva kosa, oba elipsirata.** Bitrate, kodek in pot
 * toka so se odselili v razširjen pogled (`player-full.ts`): vrstica je na
 * telefonu zanje pretesna in trdi kosi za imenom so dolgo ime potiskali z
 * zaslona. Kadar naslov zaseda skladba, stoji tu ime postaje; kadar skladbe
 * ni (govorne postaje, ustavljeno), opis **žanr · država**. Prehodna stanja
 * (povezujem, ponoven poskus, ni povezave) opis izpodrinejo.
 *
 * Ko skladbe ni, se ime postaje **ne podvoji**: takrat stoji v naslovu in iz
 * podnapisa odpade.
 */

export interface NowPlayingDeps extends VolumeDeps {
  onToggle(): void;
  onPrevious(): void;
  onNext(): void;
  /** Razpre v celozaslonski pogled (P2 §3.11, §7.4). */
  onExpand(): void;
}

/**
 * Zamik žive regije (P2 §3).
 *
 * Nekatere postaje pošiljajo ICY vsakih nekaj sekund, včasih identično.
 * Naznanilo se zato **odloži** in ga vsaka nova sprememba prestavi naprej —
 * bralnik zaslona prebere šele tisto, kar je obstalo. Brez tega postane
 * neuporaben.
 */
const ANNOUNCE_DELAY_MS = 2500;

interface StatusLine {
  /** Ime postaje — samo kadar naslov zaseda skladba; sicer `null` (je zgoraj v naslovu). */
  name: string | null;
  /** Prehodno stanje ALI opis postaje (žanr · država). Bitrate/pot sta v razširjenem pogledu. */
  note: string | null;
  live: boolean;
}

function statusFor(state: AppState, station: Station | null, lang: Lang): StatusLine {
  if (station === null)
    return { name: null, note: translate(lang, 'player.pickStation'), live: false };

  // Ime postaje pripada podnapisu samo takrat, kadar je naslov zaseden s skladbo.
  const name = state.track === null ? null : station.name;
  // Opis (žanr · država) — kadar imena ni v podnapisu (govorna postaja,
  // ustavljeno). Prazen niz (postaja brez oznak in države) pade na `null`, da
  // ne pusti za sabo praznega kosa z ločilom.
  const descr = formatStationDescription(lang, station) || null;

  switch (state.playback) {
    case 'playing':
      return { name, note: name === null ? descr : null, live: true };
    case 'loading':
      return { name, note: translate(lang, 'player.connecting'), live: false };
    case 'reconnecting':
      return { name, note: translate(lang, 'player.retrying', { n: state.attempt }), live: false };
    case 'error':
      return {
        name,
        note: translate(lang, state.error === 'blocked' ? 'player.blocked' : 'player.offline'),
        live: false,
      };
    case 'idle':
      // Stanje »ustavljeno« nosi gumb (⏵ vs ⏹), zato je tu prostor za opis.
      return { name, note: descr, live: false };
  }
}

export function createNowPlaying(
  root: HTMLElement,
  deps: NowPlayingDeps,
): { update(state: AppState): void } {
  const title = el('div', { class: 'nowplaying__title' });
  const liveLabel = el('span', { class: 'dot-live__label' });
  const live = el('span', { class: 'dot-live', hidden: '' }, [
    el('span', { class: 'dot-live__dot' }),
    liveLabel,
  ]);
  // Največ dva kosa: ime (ko naslov zaseda skladba) IN/ALI opis-stanje. Oba
  // elipsirata — za imenom ni trdih kosov, ki bi jih dolgo ime potisnilo ven.
  const statusName = el('span', { class: 'nw-player__name' });
  const statusNote = el('span', { class: 'nw-player__note' });
  const status = el('span', { class: 'nw-player__status' }, [statusName, statusNote]);

  /**
   * Značka časovnika spanja (P2 §3.8): vidna, ko je časovnik nastavljen.
   * `store/sleep.ts` vsako sekundo objavi svež `sleep`, zato odštevanje tu
   * teče brez lastnega intervala. Ikona IN čas — ne le barva.
   */
  const sleepTime = el('span', { class: 'tnum' });
  const sleep = el('span', { class: 'nw-sleep', hidden: '' }, [icon('i-clock', 14), sleepTime]);

  const sub = el('div', { class: 'nowplaying__sub' }, [live, status, sleep]);

  /**
   * Živa regija je **ločen, skrit** element in ne podnapis.
   *
   * Podnapis se spremeni ob vsakem poskusu povezave in ob vsakem prehodu
   * stanja; če bi bil `aria-live`, bi bralnik bral vsak »Povezujem…« in vsak
   * žanr. Sprememba skladbe je edino, kar je vredno naznaniti — `polite` in
   * `aria-atomic`, nikoli `assertive` (P2 §3).
   */
  const announcer = el('p', {
    class: 'nl-sr',
    role: 'status',
    'aria-live': 'polite',
    'aria-atomic': 'true',
  });

  const previous = el('button', { class: 'nowplaying__btn', type: 'button' }, [
    icon('i-skip-back', 20),
  ]);
  const toggle = el('button', { class: 'nowplaying__btn play', type: 'button' });
  const next = el('button', { class: 'nowplaying__btn', type: 'button' }, [
    icon('i-skip-forward', 20),
  ]);

  previous.addEventListener('click', deps.onPrevious);
  toggle.addEventListener('click', deps.onToggle);
  next.addEventListener('click', deps.onNext);

  /**
   * Naslovnica z nadomestkom (P2 §3.1).
   *
   * Nadomestek je **pogosto stanje, ne robni primer**: govorne postaje naslova
   * skladbe ne pošljejo, in noben vir ne pozna vsake skladbe. Zato ikona ostane
   * v DOM in slika leži čeznjo (`.nowplaying__art > img` je `position:
   * absolute` v DS) — ob neuspehu nalaganja se nadomestek pokaže sam od sebe,
   * brez naše kode.
   *
   * `alt=""` je namerno: ovitek ne nosi podatka, ki ga naslov skladbe ne bi
   * imel. Bralnik zaslona bi ime albuma prebral kot drugo skladbo.
   */
  const art = el('div', { class: 'nowplaying__art' }, [icon('i-music', 22)]);
  const cover = el('img', { alt: '', loading: 'lazy', decoding: 'async' });
  let coverSrc: string | null = null;

  function renderCover(url: string | null): void {
    if (url === coverSrc) return;
    coverSrc = url;

    if (url === null) {
      cover.remove();
      return;
    }
    cover.src = url;
    if (cover.parentNode === null) art.append(cover);
  }

  /**
   * Glasnost v vrstici (P2 §3.5) — **od 720px naprej**.
   *
   * Pod tem se skrije, ker `.nowplaying` na 390px že nosi naslovnico, dva niza
   * in tri cilje po 44px. Na telefonu jo zato nosi razdelek Zvok v Nastavitvah;
   * pravo mesto zanjo je razširjen pogled, ki čaka na DS ([D2 §38]).
   */
  const volume = createVolume(deps, 'playerVolume');
  volume.root.classList.add('nw-player__volume');

  /**
   * »Poišči skladbo« (P2 §3.12) — **od 600px naprej**.
   *
   * ⚠️ **Reža ostane, tudi kadar je meni skrit.** Skladba pride in gre (govorne
   * postaje je nimajo), gumb pa stoji pred transportom — brez rezervirane širine
   * bi se ⏮ ⏯ ⏭ premaknili vsakič, ko postaja objavi novo skladbo. P2 §3.4 je
   * pri tem izrecen: transport se postavi enkrat in se **nikoli** ne premika.
   *
   * Na telefonu isto dejanje nosi vrstica v Zgodovini (P2 §4.8) — skladba, ki
   * igra zdaj, je tam prvi vnos.
   */
  const songMenu = createSongMenu('nowplaying__btn');
  const songSlot = el('div', { class: 'nw-player__song' }, [songMenu.root]);

  /**
   * Vstop v razširjen pogled in način za avto (P2 §3.11, §7.4).
   *
   * Stoji **za** transportom in ne pred njim: tam je edino mesto, kjer ga
   * dodajanje ne premakne. Prisoten je na vsaki širini — na telefonu je
   * razširjen pogled edino mesto, kjer sta glasnost in iskanje skladbe.
   */
  const expand = el('button', { class: 'nowplaying__btn nw-player__expand', type: 'button' }, [
    icon('i-fullscreen', 20),
  ]);
  expand.addEventListener('click', deps.onExpand);

  root.append(
    art,
    el('div', { class: 'nowplaying__meta' }, [title, sub]),
    volume.root,
    songSlot,
    el('div', { class: 'nowplaying__ctrls' }, [previous, toggle, next, expand]),
    announcer,
  );

  let renderedTitle: string | null = null;
  let renderedStationId: string | null = null;

  /**
   * Kratek prehod ob **menjavi postaje** (D1, faza 7).
   *
   * Ne ob vsaki spremembi skladbe: postaja objavi novo skladbo vsake tri
   * minute in utrip ob vsaki bi bil nemir, ne prehod. Menjava postaje pa je
   * uporabnikovo dejanje in prehod mu pove, da je prijelo.
   *
   * ⚠️ **Razred je treba najprej odstraniti in vsiliti preračun**, sicer
   * brskalnik druge menjave zapored ne opazi — animacija že teče in dodajanje
   * istega razreda je brez učinka. `getBoundingClientRect()` je tu edini
   * namen: prisili preračun sloga.
   *
   * `prefers-reduced-motion` pokrije DS: `responsive.css` pri njem izniči
   * **vse** animacije, zato tu ni česa preverjati.
   */
  function markStationSwap(stationId: string | null): void {
    if (stationId === renderedStationId) return;
    const first = renderedStationId === null;
    renderedStationId = stationId;
    // Prvi izris ni menjava: aplikacija se je pravkar odprla.
    if (first || stationId === null) return;

    for (const node of [art, title]) {
      node.classList.remove('nw-player__swap');
      node.getBoundingClientRect();
      node.classList.add('nw-player__swap');
    }
  }

  /**
   * Naslov v vrstici, z marqueejem **samo ob dejanskem prekipevanju** (P2 §3.2).
   *
   * Meri se z golim besedilom: `scrollWidth > clientWidth` pove resnico šele,
   * ko ima element `white-space: nowrap` — sicer se besedilo ovije in nikoli
   * ne prekipi. Podvojeno besedilo v `.marquee__inner` zahteva DS, da je
   * pomik za −50 % brezšiven.
   *
   * `prefers-reduced-motion` obravnava DS sam: pomik ustavi in preklopi na
   * elipso (`components.css:1539`), zato tu ni česa preverjati.
   */
  function renderTitle(text: string): void {
    if (text === renderedTitle) return;
    renderedTitle = text;

    title.replaceChildren(document.createTextNode(text));
    if (title.scrollWidth <= title.clientWidth) return;

    title.replaceChildren(
      el('span', { class: 'marquee' }, [
        el('span', { class: 'marquee__inner' }, [`${text} ${text}`]),
      ]),
    );
  }

  let announceTimer: ReturnType<typeof setTimeout> | undefined;
  let announced: string | null = null;

  function announce(text: string | null): void {
    if (text === announced) return;
    announced = text;

    clearTimeout(announceTimer);
    if (text === null) {
      announcer.textContent = '';
      return;
    }
    announceTimer = setTimeout(() => {
      announcer.textContent = text;
    }, ANNOUNCE_DELAY_MS);
  }

  function announcementFor(track: Track | null, lang: Lang): string | null {
    if (track === null) return null;
    return translate(lang, 'player.announce', { track: formatTrack(lang, track) });
  }

  return {
    update(state) {
      const { lang } = state;
      // Predposluh iz zaslona Iskanje ni v uporabnikovem seznamu (P2 §2.4),
      // medijska vrstica pa mora med njegovim predvajanjem kazati njega.
      const station = findStation(playableStations(state), state.stationId);
      markStationSwap(station?.id ?? null);

      const track = state.track;
      renderTitle(
        track !== null
          ? formatTrack(lang, track)
          : (station?.name ?? translate(lang, 'player.nothing')),
      );
      announce(state.playback === 'playing' ? announcementFor(track, lang) : null);
      renderCover(state.artworkUrl);

      volume.update(state);
      // Brez skladbe iskanje nima cilja: gumb, ki odpre prazno iskanje, je
      // slabši od gumba, ki ga ni. Reža ostane in transport se ne premakne.
      songMenu.update(lang, track === null ? null : songQuery(track));

      const line = statusFor(state, station, lang);
      const setPiece = (span: HTMLSpanElement, text: string | null): void => {
        span.textContent = text ?? '';
        // [hidden] na praznem kosu: ločilo »·« (::before) tako ne ostane za sabo.
        span.toggleAttribute('hidden', text === null || text === '');
      };
      setPiece(statusName, line.name);
      setPiece(statusNote, line.note);
      liveLabel.textContent = translate(lang, 'player.live');
      live.toggleAttribute('hidden', !line.live);

      const timer = state.sleep;
      if (timer === null) {
        sleep.toggleAttribute('hidden', true);
      } else {
        const remaining = formatClock(remainingMs(timer, Date.now()));
        sleepTime.textContent = remaining;
        sleep.setAttribute('aria-label', translate(lang, 'timer.remaining', { time: remaining }));
        sleep.toggleAttribute('hidden', false);
      }

      // Gumb pove, kaj bo naredil — ne, v katerem stanju smo.
      const active =
        state.playback === 'playing' ||
        state.playback === 'loading' ||
        state.playback === 'reconnecting';
      const label = translate(lang, active ? 'player.stop' : 'player.play');
      toggle.setAttribute('aria-label', label);
      toggle.title = label;
      toggle.replaceChildren(icon(active ? 'i-stop' : 'i-play', 20));

      previous.setAttribute('aria-label', translate(lang, 'player.previous'));
      next.setAttribute('aria-label', translate(lang, 'player.next'));
      expand.setAttribute('aria-label', translate(lang, 'player.expand'));
      expand.title = translate(lang, 'player.expand');

      // Brez izbrane postaje transport ni onemogočen: prvi dotik izbere prvo.
      root.dataset.state = state.playback;
    },
  };
}
